import { MessageSquare, Phone, Mail, MapPin, Package } from 'lucide-react';
import SourceBadge from './SourceBadge';
import StatusBadge from './StatusBadge';
import { formatDate, isToday, locationLabel } from './leadUtils';

const HEADERS = ['Customer', 'Contact', 'Product / State', 'Source', 'Status', 'Date'];

export default function InquiriesTable({ rows, loading, search, onStatusChange }) {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border overflow-hidden p-16 text-center" style={{ borderColor: '#E5E7EB' }} data-testid="inquiries-table">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#0A3F22', borderTopColor: 'transparent' }} />
        <p className="text-sm" style={{ color: '#9CA3AF' }}>Loading inquiries...</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border overflow-hidden p-16 text-center" style={{ borderColor: '#E5E7EB' }} data-testid="inquiries-table">
        <MessageSquare size={40} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
        <p className="font-semibold text-sm" style={{ color: '#6B7280' }}>
          {search ? 'No results found' : 'No inquiries yet'}
        </p>
        <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
          {search ? 'Try a different search term' : 'Inquiries from your website forms will appear here'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: '#E5E7EB' }} data-testid="inquiries-table">
      <table className="w-full">
        <thead>
          <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
            {HEADERS.map((h) => (
              <th key={h} className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.id}
              data-testid={`inquiry-row-${i}`}
              className="transition-colors hover:bg-green-50"
              style={{ borderBottom: i < rows.length - 1 ? '1px solid #F3F4F6' : 'none' }}
            >
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}
                  >
                    {row.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: '#111827' }}>{row.name}</div>
                    {isToday(row.created_at) && (
                      <span className="text-xs font-semibold" style={{ color: '#D97706' }}>New today</span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-5 py-4">
                <a href={`tel:${row.phone}`} className="flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: '#0A3F22' }}>
                  <Phone size={13} />
                  {row.phone}
                </a>
                {row.email && (
                  <a href={`mailto:${row.email}`} className="flex items-center gap-1.5 text-xs mt-1 hover:underline" style={{ color: '#6B7280' }}>
                    <Mail size={11} />
                    {row.email}
                  </a>
                )}
              </td>
              <td className="px-5 py-4">
                {row.product && (
                  <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: '#374151' }}>
                    <Package size={13} style={{ color: '#0A3F22' }} />
                    {row.product}
                  </div>
                )}
                {(row.state || row.city) && (
                  <div className="flex items-center gap-1.5 text-xs mt-1" style={{ color: '#6B7280' }}>
                    <MapPin size={11} />
                    {locationLabel(row)}
                  </div>
                )}
                {row.message && (
                  <div className="text-xs mt-1 italic" style={{ color: '#9CA3AF' }}>
                    "{row.message.slice(0, 60)}{row.message.length > 60 ? '...' : ''}"
                  </div>
                )}
              </td>
              <td className="px-5 py-4">
                <SourceBadge source={row.source} />
              </td>
              <td className="px-5 py-4">
                <StatusBadge
                  status={row.status}
                  onChange={(next) => onStatusChange(row.id, next)}
                  testId={`inquiry-status-${i}`}
                />
              </td>
              <td className="px-5 py-4">
                <div className="text-xs" style={{ color: '#6B7280' }}>{formatDate(row.created_at)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
